// src/components/CookieBanner.jsx
// Shows once on first visit — stores consent in localStorage
// Complies with GDPR, CCPA, and India DPDP Act 2023

import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

const CONSENT_KEY = 'tecfin_cookie_consent'

export default function CookieBanner() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Show banner only if user hasn't consented yet
    const saved = localStorage.getItem(CONSENT_KEY)
    if (!saved) setVisible(true)
  }, [])

  const accept = () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ accepted: true, date: new Date().toISOString() }))
    setVisible(false)
  }

  const decline = () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ accepted: false, date: new Date().toISOString() }))
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9998,
        background: 'var(--brand-900)',
        borderTop: '1px solid rgba(255,255,255,.1)',
        padding: '14px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
        boxShadow: '0 -4px 20px rgba(0,0,0,.25)',
      }}
    >
      {/* Icon */}
      <i
        className="ti ti-cookie"
        style={{ fontSize: 22, color: 'var(--brand-400)', flexShrink: 0 }}
        aria-hidden="true"
      />

      {/* Message */}
      <p style={{
        flex: 1,
        fontSize: 13,
        color: '#c5d8f5',
        margin: 0,
        minWidth: 200,
        lineHeight: 1.5,
      }}>
        We use essential cookies to keep you logged in and remember your preferences.
        We do not use advertising or tracking cookies.{' '}
        <Link
          to="/legal#privacy"
          style={{ color: 'var(--brand-400)', textDecoration: 'underline', fontWeight: 500 }}
        >
          Privacy policy
        </Link>
      </p>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button
          onClick={decline}
          style={{
            padding: '7px 14px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,.2)',
            background: 'transparent',
            color: '#6a8ab8',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'inherit',
            transition: 'all .15s',
            whiteSpace: 'nowrap',
          }}
        >
          Decline
        </button>
        <button
          onClick={accept}
          style={{
            padding: '7px 16px',
            borderRadius: 6,
            border: 'none',
            background: 'var(--brand-600)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            transition: 'all .15s',
            whiteSpace: 'nowrap',
          }}
        >
          Accept
        </button>
      </div>
    </div>
  )
}

/**
 * Helper — check if user has accepted cookies
 * Use this anywhere you need to conditionally load analytics
 */
export function hasConsented() {
  try {
    const saved = localStorage.getItem(CONSENT_KEY)
    if (!saved) return false
    return JSON.parse(saved).accepted === true
  } catch {
    return false
  }
}
